import {ChangeDetectionStrategy, Component, inject, input, output} from '@angular/core';
import {DateInputDirective} from "../../../../../../shared/directives/date-input.directive";
import {FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatButton} from "@angular/material/button";
import {
  MatDatepicker,
  MatDatepickerModule
} from "@angular/material/datepicker";
import {MatFormField, MatInput, MatLabel, MatSuffix} from "@angular/material/input";
import {MatOption} from "@angular/material/core";
import {MatSelect} from "@angular/material/select";
import {NumericInputDirective} from "../../../../../../shared/directives/numeric-input.directive";
import {PhoneMaskDirective} from "../../../../../../shared/directives/phone-mask.directive";
import {RecordData, SelectData} from "../../../../../types/records-table.types";
import {BankDetailsDialogData} from "../../types/bank-details-modal.types";
import {FormFieldUpdate} from "../../../../../types/file.types";
import {BankDetailsFormField} from "../../types/bank-details-form-field.types";
import {bankDetailsFormFields, bankDetailsRowKeys} from "../../configs/bank-details-form-fields";
import {defaultRecordData} from "../../../../../models/default-record-data";
import {UtilityService} from "../../../../../services/utility.service";
import {BankDetailsModalService} from "../../services/bank-details-modal.service";

@Component({
  selector: 'div[app-bank-details-form]',
  imports: [
    DateInputDirective,
    FormsModule,
    MatButton,
    MatDatepickerModule,
    MatFormField,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    MatSuffix,
    NumericInputDirective,
    PhoneMaskDirective,
    ReactiveFormsModule
  ],
  templateUrl: './bank-details-form.component.html',
  styleUrl: './bank-details-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BankDetailsFormComponent {
  formGroup = input<FormGroup>(new FormGroup({}));
  bankDetailsData = input<RecordData>(defaultRecordData);
  bankDetailsDialogData = input<BankDetailsDialogData>();
  legalEntityTypes = input<Array<SelectData>>([]);
  currentBankDetailsIndex = input<number>(0);
  selectedTabIndex = input<number>(0);
  isEditModeActive = input<boolean>(false);
  isNew = input<boolean>(false);
  handleRecordUpdate = output<FormFieldUpdate>();
  deleteBankDetailsClick = output<number>();
  handleAttachClick = output<void>();

  private utilityService: UtilityService = inject(UtilityService);
  private bankDetailsModalService: BankDetailsModalService = inject(BankDetailsModalService);

  public readonly bankDetailsRowKeys = bankDetailsRowKeys;

  public getClasses(fieldName: string): string {
    const btnClass = this.formGroup().get('passportExist')?.value ? 'success' : 'warn';
    const commentClass = fieldName === 'comment' ? 'comment' : '';
    if (!this.isEditModeActive()) { return `none-pointer-events ${btnClass} ${commentClass}`; }

    return `${btnClass} ${commentClass}`;
  }

  public handleInputChange(key: string): void {
    if (this.isNew()) { return; }

    const newValue = this.formGroup().get(key)?.value;
    this.handleRecordUpdate.emit({ key, newValue })
  }

  public getFieldsByRowKey(rowKey: string): Array<BankDetailsFormField> {
    return bankDetailsFormFields.filter(field => field.rowKey === rowKey);
  }

  public handleCheckboxChange(key: string): void {
    const currentPassportExist = this.formGroup().get(key)?.value;

    if (this.isNew()) {
      this.formGroup().get(key)?.setValue(!currentPassportExist);
      return;
    }

    this.bankDetailsData()[key] = !currentPassportExist;
    this.handleRecordUpdate.emit({ key, newValue: !currentPassportExist })
  }

  public handleDateChange(date: Date, key: string, picker: MatDatepicker<Date>): void {
    if (!this.isBirthDateValid(picker, key)) { return; }
    if (this.isNew()) { return; }

    const formattedDate = date ? this.utilityService.formatDateToRussianStyle(date) : null;
    this.handleRecordUpdate.emit({ key, newValue: formattedDate });
  }

  public handleDeleteBankDetails(index: number): void {
    this.deleteBankDetailsClick.emit(index);
  }

  private isBirthDateValid(picker: MatDatepicker<Date>, key: string): boolean {
    const isValidData = this.bankDetailsModalService.isBirthDateValid(this.formGroup());
    if (isValidData) { return true; }

    this.formGroup().get(key)?.setValue(this.bankDetailsData()[key]);
    picker.close();
    return false;
  }
}
